import readXlsxFile, { Row } from 'read-excel-file';
import type { DataValidation } from 'exceljs';
import { ExcelTemplateOptions } from '../models/excel-review.model';

type WorkbookConstructor = typeof import('exceljs').Workbook;

const defaultTemplateDataRowCount = 200;
const defaultTemplateColumnWidth = 34;

export function readExcelFile(file: File): Promise<Row[]> {
  return readXlsxFile(file).catch((error) => {
    console.error('Error reading Excel file:', error);
    return Promise.reject(error);
  });
}

export function toCellString(cell: unknown): string {
  return cell === null || cell === undefined ? '' : `${cell}`.trim();
}

export function splitCellList(value: string): string[] {
  return (value ?? '')
    .split(/[,;\n]/)
    .map(entry => entry.trim())
    .filter(entry => entry.length > 0);
}

export function containsIgnoreCase(values: string[], value: string): boolean {
  return values.some(existing => existing.toLowerCase() === value.toLowerCase());
}

export function toWholeNumber(value: string): number | null {
  const text = (value ?? '').trim().replace(/[\s,]/g, '');
  if (!/^-?\d+$/.test(text)) return null;
  return Number(text);
}

export function findClosestName(value: string, candidates: string[]): string | null {
  const target = value.trim().toLowerCase();
  if (!target) return null;

  let best: string | null = null;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    const distance = levenshteinDistance(target, candidate.trim().toLowerCase());
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }

  const threshold = Math.max(2, Math.floor(target.length / 3));
  return best !== null && bestDistance <= threshold ? best : null;
}

function levenshteinDistance(a: string, b: string): number {
  if (!a.length || !b.length) return Math.max(a.length, b.length);

  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    previous = current;
  }
  return previous[b.length];
}

function columnLetter(column: number): string {
  let letters = '';
  let remaining = column;
  while (remaining > 0) {
    const index = (remaining - 1) % 26;
    letters = String.fromCharCode(65 + index) + letters;
    remaining = Math.floor((remaining - 1) / 26);
  }
  return letters;
}

async function loadWorkbookConstructor(): Promise<WorkbookConstructor> {
  const exceljs = await import('exceljs') as unknown as {
    Workbook?: WorkbookConstructor;
    default?: { Workbook?: WorkbookConstructor };
  };

  const Workbook = exceljs.Workbook ?? exceljs.default?.Workbook;
  if (!Workbook) {
    throw new Error('Failed to load exceljs Workbook.');
  }

  return Workbook;
}

export async function downloadExcelTemplateAsync(options: ExcelTemplateOptions): Promise<void> {
  const Workbook = await loadWorkbookConstructor();
  const workbook = new Workbook();

  const sheet = workbook.addWorksheet(options.sheetName);
  sheet.columns = options.columns.map(() => ({ width: options.columnWidth ?? defaultTemplateColumnWidth }));

  const headerTitleFont = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
  options.columns.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = header.subtitle
      ? {
        richText: [
          { text: header.title, font: headerTitleFont },
          { text: `\n${header.subtitle}`, font: { color: { argb: 'FFD6E2F0' }, size: 9 } }
        ]
      }
      : header.title;
    if (!header.subtitle) {
      cell.font = headerTitleFont;
    }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4368' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  });
  sheet.getRow(1).height = 34;

  const lists = workbook.addWorksheet('Lists', { state: 'veryHidden' });
  let nextListColumn = 1;

  for (const list of options.namedLists ?? []) {
    const column = nextListColumn++;
    list.values.forEach((value, index) => lists.getCell(index + 1, column).value = value);
    const letter = columnLetter(column);
    workbook.definedNames.add(`Lists!$${letter}$1:$${letter}$${Math.max(1, list.values.length)}`, list.name);
  }

  for (const table of options.lookupTables ?? []) {
    const width = Math.max(1, ...table.rows.map(row => row.length));
    const firstColumn = nextListColumn;
    nextListColumn += width;
    table.rows.forEach((row, rowIndex) =>
      row.forEach((value, columnIndex) => lists.getCell(rowIndex + 1, firstColumn + columnIndex).value = value));
    const firstLetter = columnLetter(firstColumn);
    const lastLetter = columnLetter(firstColumn + width - 1);
    workbook.definedNames.add(`Lists!$${firstLetter}$1:$${lastLetter}$${Math.max(1, table.rows.length)}`, table.name);
  }

  const lastDataRow = (options.dataRowCount ?? defaultTemplateDataRowCount) + 1;
  const dataValidations = (sheet as unknown as {
    dataValidations: { add(range: string, validation: DataValidation): void }
  }).dataValidations;

  for (const entry of options.validations ?? []) {
    const letter = columnLetter(entry.column);
    dataValidations.add(`${letter}2:${letter}${lastDataRow}`, entry.validation);
  }

  for (const format of options.columnFormats ?? []) {
    for (let rowNumber = 2; rowNumber <= lastDataRow; rowNumber++) {
      sheet.getCell(rowNumber, format.column).numFmt = format.numFmt;
    }
  }

  const gridlineBorder = { style: 'thin' as const, color: { argb: 'FFD9D9D9' } };
  for (let rowNumber = 2; rowNumber <= lastDataRow; rowNumber++) {
    for (const columnNumber of options.highlightedColumns ?? []) {
      const cell = sheet.getCell(rowNumber, columnNumber);
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFEAF1FB' }
      };
      cell.border = {
        top: gridlineBorder,
        left: gridlineBorder,
        bottom: gridlineBorder,
        right: gridlineBorder
      };
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = options.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  setTimeout(() => URL.revokeObjectURL(url), 0);
}